import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon.jsx';
import { BedMapsIcon, ButtonLink, Check, Logo, MenuItem } from './parts.jsx';
import { KITCHEN_TAP_PHOTO, howItWorksLink, menuForPath, menus, selfReferLink, unsplash } from './navigation.js';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';

const HOVER_OPEN_DELAY = 90;
const HOVER_CLOSE_DELAY = 180;

/** The highlighted tile on the right of each menu. */
function MenuFeature({ menu, onNavigate }) {
  const { feature } = menu;
  return <div className={`mk-menu-feature is-${feature.kind}`}>
    {feature.kind === 'icon' && <span className="mk-menu-feature-icon"><Icon name={feature.icon} size={26}/></span>}
    {feature.kind === 'photo' && <img className="mk-menu-feature-photo" src={unsplash(KITCHEN_TAP_PHOTO, 640)} alt="" width="284" height="132" loading="lazy"/>}
    {feature.kind === 'bedmaps' && <BedMapsIcon size={44}/>}
    <p className="mk-menu-feature-title">{feature.title}</p>
    <p className="mk-menu-feature-text">{feature.text}</p>
    {feature.check && <Check>{feature.check}</Check>}
    <Link className="mk-button is-primary is-h52 mk-menu-feature-action" to={feature.to} onClick={onNavigate}>{feature.action}</Link>
  </div>;
}

function MenuItems({ menu, onNavigate }) {
  return <div className="mk-menu-items">
    {menu.group && <p className="mk-menu-group">{menu.group}</p>}
    <ul className="mk-menu-grid">{menu.items.map((item) => <li key={item.title}><MenuItem item={item} onNavigate={onNavigate}/></li>)}</ul>
  </div>;
}

/** One menu in the phone navigation: a heading that opens its links. */
function MobileGroup({ menu, open, onToggle, onNavigate }) {
  return <div className="mk-mobile-group">
    <button type="button" className="mk-mobile-group-button" aria-expanded={open} aria-controls={`mobile-${menu.id}`} onClick={onToggle}>
      <span>{menu.label}</span><Icon name="chevron-down" size={20} className="mk-mobile-chevron"/>
    </button>
    <div className="mk-mobile-group-body" id={`mobile-${menu.id}`} hidden={!open}>
      <MenuItems menu={menu} onNavigate={onNavigate}/>
      <ButtonLink to={menu.feature.to} variant="soft" className="mk-mobile-feature-action">{menu.feature.action}</ButtonLink>
    </div>
  </div>;
}

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroups, setMobileGroups] = useState(() => new Set());
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const buttonRefs = useRef({});
  const hoverTimer = useRef(0);
  const hoverOpened = useRef({ id: null, at: 0 });
  const current = menuForPath(location.pathname);
  const account = user ? { to: '/account', label: 'Your account' } : { to: '/sign-in', label: 'Sign in' };

  useEffect(() => { setOpenMenu(null); setMobileOpen(false); }, [location.pathname, location.hash]);
  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  useEffect(() => {
    if (!openMenu && !mobileOpen) return undefined;
    const closeOnOutsidePress = (event) => { if (openMenu && !headerRef.current?.contains(event.target)) setOpenMenu(null); };
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (openMenu) { buttonRefs.current[openMenu]?.focus(); setOpenMenu(null); }
      if (mobileOpen) { setMobileOpen(false); toggleRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => { document.removeEventListener('pointerdown', closeOnOutsidePress); document.removeEventListener('keydown', closeOnEscape); };
  }, [openMenu, mobileOpen]);

  // While the phone menu covers the page, the page behind it cannot be scrolled or reached with the keyboard.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const behind = [document.getElementById('main-content'), document.querySelector('.mk-footer')].filter(Boolean);
    behind.forEach((element) => element.setAttribute('inert', ''));
    document.documentElement.classList.add('mk-menu-locked');
    const closeOnWideScreen = window.matchMedia('(min-width: 1280px)');
    const onChange = (event) => { if (event.matches) setMobileOpen(false); };
    closeOnWideScreen.addEventListener('change', onChange);
    return () => {
      behind.forEach((element) => element.removeAttribute('inert'));
      document.documentElement.classList.remove('mk-menu-locked');
      closeOnWideScreen.removeEventListener('change', onChange);
    };
  }, [mobileOpen]);

  const later = (action, delay) => { window.clearTimeout(hoverTimer.current); hoverTimer.current = window.setTimeout(action, delay); };
  const hoverOpen = (event, id) => {
    if (event.pointerType !== 'mouse') return;
    later(() => { hoverOpened.current = { id, at: Date.now() }; setOpenMenu(id); }, openMenu ? 0 : HOVER_OPEN_DELAY);
  };
  const hoverClose = (event) => { if (event.pointerType === 'mouse') later(() => setOpenMenu(null), HOVER_CLOSE_DELAY); };
  const keepOpen = () => window.clearTimeout(hoverTimer.current);
  const toggle = (id) => {
    window.clearTimeout(hoverTimer.current);
    const justOpenedByHover = hoverOpened.current.id === id && Date.now() - hoverOpened.current.at < 700;
    setOpenMenu((open) => (open === id && !justOpenedByHover ? null : id));
    hoverOpened.current = { id: null, at: 0 };
  };
  // Keyboard focus moving out of the header closes the menu. A click on empty space is left to the outside-press handler.
  const closeOnFocusLeave = (event) => { if (openMenu && event.relatedTarget && !headerRef.current?.contains(event.relatedTarget)) setOpenMenu(null); };
  // Focus moving to another part of the header (the next menu button, Sign in) closes the open menu too.
  const closeOnFocusElsewhere = (event) => { if (openMenu && !event.target.closest(`[data-menu="${openMenu}"]`)) setOpenMenu(null); };
  const closeAll = () => { setOpenMenu(null); setMobileOpen(false); };
  const signOut = async () => {
    closeAll();
    await supabase?.auth.signOut();
    navigate('/', { replace: true });
  };
  const toggleGroup = (id) => setMobileGroups((open) => { const next = new Set(open); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  return <>
    <header className={`mk-header ${mobileOpen ? 'is-mobile-open' : ''}`} ref={headerRef} onBlur={closeOnFocusLeave} onFocus={closeOnFocusElsewhere}>
      <a className="mk-skip-link" href="#main-content">Skip to main content</a>
      <div className="mk-container mk-header-bar">
        <Link className="mk-header-logo" to="/" aria-label="Resident Service 360 home" onClick={closeAll}><Logo/></Link>
        <nav className="mk-nav" aria-label="Main">
          <ul className="mk-nav-list">
            {menus.map((menu) => <li key={menu.id} data-menu={menu.id}>
              <button type="button" ref={(element) => { buttonRefs.current[menu.id] = element; }}
                className={`mk-nav-item ${current === menu.id ? 'is-current' : ''} ${openMenu === menu.id ? 'is-open' : ''}`}
                aria-expanded={openMenu === menu.id} aria-controls={`menu-${menu.id}`}
                onClick={() => toggle(menu.id)} onPointerEnter={(event) => hoverOpen(event, menu.id)} onPointerLeave={hoverClose}>
                {menu.label}
              </button>
              {/* The panel follows its button so Tab moves straight into it. It is positioned against the header. */}
              <div className="mk-menu-panel" id={`menu-${menu.id}`} hidden={openMenu !== menu.id} onPointerEnter={keepOpen} onPointerLeave={hoverClose}>
                <div className="mk-container">
                  <div className="mk-menu-card">
                    <MenuItems menu={menu} onNavigate={closeAll}/>
                    <MenuFeature menu={menu} onNavigate={closeAll}/>
                  </div>
                </div>
              </div>
            </li>)}
            <li><NavLink className="mk-nav-item" to={howItWorksLink.to} onPointerEnter={(event) => event.pointerType === 'mouse' && openMenu && later(() => setOpenMenu(null), 0)}>{howItWorksLink.label}</NavLink></li>
          </ul>
        </nav>
        <div className="mk-header-actions">
          <NavLink className="mk-button is-primary is-h44 mk-self-referral-cta" to={selfReferLink.to} onClick={closeAll} onPointerEnter={(event) => event.pointerType === 'mouse' && openMenu && later(() => setOpenMenu(null), 0)}>{selfReferLink.label}<Icon name="arrow-right" size={18}/></NavLink>
          {user
            ? (
              <div className="mk-account" data-menu="account">
                <button type="button" ref={(element) => { buttonRefs.current.account = element; }} className="mk-button is-soft is-h44 mk-header-account"
                  aria-expanded={openMenu === 'account'} aria-controls="menu-account" onClick={() => toggle('account')}>
                  {account.label}
                </button>
                <div className="mk-account-menu" id="menu-account" hidden={openMenu !== 'account'}>
                  <p className="mk-account-who">{`Signed in as ${user.email}`}</p>
                  <Link className="mk-account-item" to={account.to} onClick={closeAll}>{account.label}</Link>
                  <button type="button" className="mk-account-item" onClick={() => void signOut()}>Sign out</button>
                </div>
              </div>
            )
            : <Link className="mk-button is-soft is-h44 mk-header-account" to={account.to}>{account.label}</Link>}
        <button type="button" className="mk-menu-toggle" ref={toggleRef} aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => setMobileOpen((open) => !open)}>
          <Icon name={mobileOpen ? 'x' : 'menu'} size={22}/><span className="visually-hidden">Menu</span>
        </button>
        </div>
      </div>

      <div className="mk-mobile-menu" id="mobile-menu" hidden={!mobileOpen}>
        <nav className="mk-container mk-mobile-nav" aria-label="Main">
          {menus.map((menu) => <MobileGroup key={menu.id} menu={menu} open={mobileGroups.has(menu.id)} onToggle={() => toggleGroup(menu.id)} onNavigate={closeAll}/>)}
          <Link className="mk-mobile-link" to={howItWorksLink.to} onClick={closeAll}>{howItWorksLink.label}<Icon name="chevron-right" size={20}/></Link>
          <Link className="mk-button is-soft is-h56 is-17 is-block mk-mobile-account" to={account.to} onClick={closeAll}>{account.label}</Link>
          {user && (
            <>
              <p className="mk-account-who">{`Signed in as ${user.email}`}</p>
              <button type="button" className="mk-mobile-link mk-mobile-signout" onClick={() => void signOut()}>Sign out</button>
            </>
          )}
        </nav>
      </div>
    </header>
    {openMenu && openMenu !== 'account' && <div className="mk-dim" aria-hidden="true" onPointerDown={() => setOpenMenu(null)}/>}
  </>;
}
